import { Link } from 'react-router-dom'
import { formatDate, formatRelative } from '../../lib/format'
import { acquaintanceTitle, directionWords } from '../../lib/outreach'
import type { OutreachAcquaintance, OutreachHistoryEntry, PersonNote } from '../../types/outreach'

// The three parts of the drawer that are pure renders of what the API said. Kept apart from the drawer
// itself so each state — you know them / you do not, the history both ways, the brief and its stale
// warning — can be rendered and checked without a query client behind it.

/** The plain answer to "do I really know them?" — with the counts and dates it rests on. */
export function KnowThem({ a, email }: { a: OutreachAcquaintance; email?: string | null }) {
  return (
    <div className={`outreach-know ${a.verdict}`}>
      <strong>{acquaintanceTitle(a.verdict)}</strong>
      <p>{a.detail}</p>
      <p className="muted small">
        {a.ownMessages} from you · {a.inboundMessages} from them · {a.meetings} {a.meetings === 1 ? 'meeting' : 'meetings'}
        {a.firstContact && ` · first ${formatDate(a.firstContact)}`}
        {a.lastContact && ` · last ${formatDate(a.lastContact)}`}
        {email && ` · ${email}`}
      </p>
    </div>
  )
}

/** The history, newest first: mail both ways and meetings, each line saying which way it went. */
export function History({ history }: { history: OutreachHistoryEntry[] }) {
  return (
    <ol className="outreach-history">
      {history.map((h, i) => (
        <li key={`${h.at}-${i}`} className={`outreach-history-line ${h.direction}`}>
          <time>{formatRelative(h.at)}</time>
          <div>
            <span className="kicker">{directionWords(h.direction)}</span>
            <strong>{h.subject}</strong>
            {h.snippet && <span className="muted small">{h.snippet}</span>}
            {h.engagementId && <Link className="small" to={`/opportunities/${encodeURIComponent(h.engagementId)}`}>on the pursuit</Link>}
          </div>
        </li>
      ))}
    </ol>
  )
}

/** The brief. A stale one says so at the top rather than reading as current. */
export function Brief({ note, writtenAt, modelId, stale }: { note: PersonNote; writtenAt: string; modelId: string; stale: boolean }) {
  return (
    <div className={`research ${stale ? 'stale' : ''}`}>
      {stale && <p className="outreach-stale">Newer mail has arrived since this was written. Research again to bring it up to date.</p>}
      <p className="research-summary">{note.whoTheyAre}</p>
      <p>{note.whatTheyDo}</p>

      <h4>What they care about</h4>
      {note.caresAbout.length === 0 ? <p className="muted small">Nothing the file establishes in their own words.</p> : (
        <ul className="chips">{note.caresAbout.map((c) => <li key={c} className="chip on">{c}</li>)}</ul>
      )}

      <h4>How you have engaged</h4>
      {note.timeline.length === 0 ? <p className="muted small">Nothing dated on file.</p> : (
        <ol className="outreach-timeline">{note.timeline.map((m) => <li key={m.when + m.what}><time>{m.when}</time><span>{m.what}</span></li>)}</ol>
      )}

      <h4>What to say next</h4>
      {note.openers.length === 0 ? <p className="muted small">The file does not carry enough to open with.</p> : (
        <ul className="outreach-openers">{note.openers.map((o) => <li key={o.line}><q>{o.line}</q><span className="muted small">{o.why}</span></li>)}</ul>
      )}

      <h4>Risks</h4>
      {note.risks.length === 0 ? <p className="muted small">None the file shows.</p> : <ul className="research-risks">{note.risks.map((r) => <li key={r}>{r}</li>)}</ul>}

      {note.notOnFile.length > 0 && (
        <div className="research-gaps">
          <strong>Not on file</strong>
          <ul>{note.notOnFile.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
      )}
      <details className="research-grounding">
        <summary>Grounded on</summary>
        <ul>{note.groundedOn.map((g) => <li key={g}>{g}</li>)}</ul>
      </details>
      <p className="muted small research-stamp">Written {formatDate(writtenAt)} by {modelId}.</p>
    </div>
  )
}
